import React, { useState } from 'react';
import {
  confirmErpPasswordReset,
  requestErpPasswordReset
} from '../services/erpSessionClient';
import {
  ERP_USERNAME_HELP,
  isValidErpUsername,
  normalizeErpUsername
} from '../services/erpUsername';
import '../styles/Login.css';

const NIVORA_LOGO = `${process.env.PUBLIC_URL || ''}/assets/nivora-logo.png`;

export function passwordResetTokenFromHash(hash) {
  const query = String(hash || '').split('?')[1] || '';
  return new URLSearchParams(query).get('token') || '';
}

function AccessPage({ children, title, description }) {
  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <img className="login-product-logo" src={NIVORA_LOGO} alt="Nivora ERP" />
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [tenantSlug, setTenantSlug] = useState('');
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');
    const normalizedUsername = normalizeErpUsername(username);
    if (!isValidErpUsername(normalizedUsername)) {
      setError(ERP_USERNAME_HELP);
      return;
    }
    setLoading(true);
    try {
      const result = await requestErpPasswordReset({
        tenantSlug: tenantSlug.trim().toLowerCase(),
        username: normalizedUsername
      });
      setMessage(result?.message || 'Se a conta existir, enviaremos as instruções em alguns minutos.');
    } catch (requestError) {
      setError(requestError?.message || 'Não foi possível solicitar a recuperação agora.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccessPage
      title="Recuperar acesso"
      description="Enviaremos um link seguro ao e-mail cadastrado."
    >
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {message && <div className="login-success-note" role="status">{message}</div>}
      {!message && (
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label" htmlFor="recovery-tenant">Empresa</label>
            <input
              id="recovery-tenant"
              className="form-control"
              value={tenantSlug}
              onChange={(event) => setTenantSlug(event.target.value)}
              placeholder="ex.: minha-empresa"
              pattern="[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*"
              maxLength="80"
              autoComplete="organization"
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="recovery-user">Usuário ou e-mail de acesso</label>
            <input
              id="recovery-user"
              type="text"
              className="form-control"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              maxLength="80"
              autoComplete="username"
              aria-describedby="recovery-user-help"
              required
            />
            <small id="recovery-user-help" className="login-field-help">{ERP_USERNAME_HELP}</small>
          </div>
          <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
            {loading ? 'Enviando...' : 'Enviar link seguro'}
          </button>
        </form>
      )}
      <p className="login-help"><a href="/login">Voltar ao login</a></p>
    </AccessPage>
  );
}

export function ResetPassword() {
  const token = passwordResetTokenFromHash(window.location.hash);
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? '' : 'Este link de redefinição está incompleto.');
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    if (newPassword !== confirmation) {
      setError('As senhas informadas não são iguais.');
      return;
    }
    setLoading(true);
    try {
      await confirmErpPasswordReset({ token, newPassword });
      setDone(true);
    } catch (resetError) {
      setError(resetError?.message || 'O link é inválido ou expirou.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccessPage
      title={done ? 'Senha atualizada' : 'Criar nova senha'}
      description={done ? 'Seu acesso está pronto novamente.' : 'Use pelo menos 10 caracteres.'}
    >
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {done ? (
        <a className="btn btn-primary btn-lg signup-submit" href="/login">Entrar no Nivora</a>
      ) : (
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label" htmlFor="reset-password">Nova senha</label>
            <input
              id="reset-password"
              type="password"
              className="form-control"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              minLength="10"
              maxLength="200"
              disabled={!token}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="reset-confirmation">Confirmar nova senha</label>
            <input
              id="reset-confirmation"
              type="password"
              className="form-control"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
              minLength="10"
              maxLength="200"
              disabled={!token}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary btn-lg" disabled={loading || !token}>
            {loading ? 'Atualizando...' : 'Atualizar senha'}
          </button>
        </form>
      )}
      {!done && <p className="login-help"><a href="/login">Voltar ao login</a></p>}
    </AccessPage>
  );
}
